/**
 * Generic API Response Model
 * Matches the backend response structure:
 * {
 *   responseCode: string,
 *   responseMessage: string,
 *   responseDescription: string,
 *   data: T (generic type)
 * }
 */

/**
 * Generic API Response Type
 * @template T - The type of data in the response
 */
export class ApiResponse {
  /**
   * @param {string} responseCode - Response code (e.g., "200" for success)
   * @param {string} responseMessage - Response message
   * @param {string} responseDescription - Detailed description
   * @param {T} data - The actual data payload
   */
  constructor(responseCode, responseMessage, responseDescription, data) {
    this.responseCode = responseCode;
    this.responseMessage = responseMessage;
    this.responseDescription = responseDescription;
    this.data = data;
  }

  /**
   * Check if the response is successful
   * @returns {boolean}
   */
  isSuccess() {
    return this.responseCode === '200';
  }

  /**
   * Get error message if response failed
   * @returns {string|null}
   */
  getErrorMessage() {
    if (this.isSuccess()) return null;
    return this.responseMessage || this.responseDescription || 'An error occurred';
  }
}

/**
 * Helper function to parse API response
 * @template T
 * @param {Object} response - Axios response object
 * @returns {ApiResponse<T>}
 */
export const parseApiResponse = (response) => {
  const payload = response?.data !== undefined ? response.data : response;
  if (!payload || typeof payload !== 'object') {
    return new ApiResponse(undefined, undefined, undefined, null);
  }

  const responseCode = payload.responseCode ?? payload.ResponseCode;
  const responseMessage = payload.responseMessage ?? payload.ResponseMessage;
  const responseDescription = payload.responseDescription ?? payload.ResponseDescription;
  const data = payload.data ?? payload.Data;

  return new ApiResponse(responseCode, responseMessage, responseDescription, data);
};

/**
 * Helper function to check if response is successful
 * @param {Object} response - Axios response or ApiResponse
 * @returns {boolean}
 */
export const isSuccessResponse = (response) => {
  if (response instanceof ApiResponse) {
    return response.isSuccess();
  }
  const parsed = parseApiResponse(response);
  return parsed.isSuccess();
};

/**
 * Helper function to extract data from response
 * @template T
 * @param {Object} response - Axios response or ApiResponse
 * @returns {T|null}
 */
export const extractData = (response) => {
  if (response instanceof ApiResponse) {
    return response.data;
  }
  const parsed = parseApiResponse(response);
  return parsed.data;
};

/**
 * Helper function to get error message from response
 * @param {Object} response - Axios response or ApiResponse
 * @returns {string|null}
 */
export const getErrorMessage = (response) => {
  if (response instanceof ApiResponse) {
    return response.getErrorMessage();
  }
  const parsed = parseApiResponse(response);
  return parsed.getErrorMessage();
};

export default ApiResponse;

